import { getQuote } from '@/pages/Quotes/services';
import { Quote } from '@/pages/Quotes/types';
import { getQuoteFromLocalStorage, cacheQuoteToLocalStorage } from '@/utils/quotes';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState, ReactNode, FC, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';
import { routes } from '@/config/routes';

let prevCCState = false;

type Props = {
    selectedQuote: Quote | undefined;
    quoteDetails: Quote | undefined;
    setQuote: (quote: Quote) => void;
    refetchQuoteDetails: () => void;
}

const GlobalContext = createContext<Props | undefined>(undefined);

interface GlobalContextProps {
    children: ReactNode;
}

export const GlobalContextProvider: FC<GlobalContextProps> = ({ children }) => {
    const { user } = useAuth();
    const [selectedQuote, setQuoteState] = useState<Quote | undefined>();
    // const prevCCState = useRef<Quote | undefined>();

    const { pathname } = useLocation();

    const isCommandCenter = useMemo(() => {
        return pathname.includes(routes.comandCenter.root) || pathname.includes(routes.myWorkspace.prefix);
    }, [pathname]);

    const cachedQuote = useMemo(() => {
        if (!user?.accountId) return undefined;
        return getQuoteFromLocalStorage(user.accountId, isCommandCenter);
    }, [user, isCommandCenter]);

    // Wrapper for setSelectedQuote that also saves to localStorage
    const setQuote = useCallback((quote: Quote) => {
        if (!user?.accountId) return;
        cacheQuoteToLocalStorage(user.accountId, quote, isCommandCenter);
        setQuoteState(quote);
        prevCCState = isCommandCenter; // change state only when set proper quote to state, so we have right quote is while switching from cc to partner products pages
    }, [user, isCommandCenter]);

    // console.log('--------------------------------------------------');

    const quoteId = useMemo(() => {
        let res = selectedQuote?.id || cachedQuote?.id || '';
        if (prevCCState !== isCommandCenter) {
            res = cachedQuote?.id || '';
        }
        return res;
    }, [cachedQuote, selectedQuote, isCommandCenter]);

    // console.log('selectedQuote ID', selectedQuote?.id);
    // console.log('cachedQuote ID', cachedQuote?.id);
    // console.log('quoteId', quoteId);

    // Fetch fresh quote details from the server
    const { data: quoteDetails, refetch: refetchQuoteDetails } = useQuery({
        queryKey: ['quote', quoteId],
        queryFn: () => getQuote(quoteId),
        enabled: !!quoteId,
    });

    return (
        <GlobalContext.Provider value={{ selectedQuote, setQuote, refetchQuoteDetails, quoteDetails }}>
            {children}
        </GlobalContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useGlobalContext = () => {
    const context = useContext(GlobalContext);
    if (!context) {
        throw new Error('useGlobalContext must be used within a GlobalContextProvider');
    }
    return context;
};
